<template>
  <modal name="modal-asset-tracker-notification-setting" class="asset-tracker-notification-setting modal-inner" transition="pop-out" :width="modalWidth" :focus-trap="true" :min-height="600" height="auto" :scrollable="true" :resizable="true" :adaptive="true" :reset="true" :clickToClose="false" @before-close="reset">
    <span class="close-button" @click="hide()"><BaseIcon icon="times-circle" class="text-white"/></span>
    <div class="modal-header less-margin-bottom text-lg bg-primary-50 font-serif">
      {{ $t('secure_route_notification_settings.modal_title') }}
    </div>
    <div class="modal-body less-side-padding responsive-height">
    
      <!-- Header with company info -->
      <div class="company-header-card">
        <div class="company-info-row">
          <div class="company-info-item">
            <div class="company-info-icon">
              <BaseIcon icon="building" />
            </div>
            <div class="company-info-text">
              <div class="info-label">{{ $t('secure_route_notification_settings.company') }}</div>
              <div class="info-value">{{ customer?.name || '-' }}</div>
            </div>
          </div>
          <div class="company-info-item">
            <div class="company-info-icon">
              <BaseIcon icon="envelope" />
            </div>
            <div class="company-info-text">
              <div class="info-label">{{ $t('secure_route_notification_settings.email') }}</div>
              <div class="info-value">{{ customer?.email || '-' }}</div>
            </div>
          </div>
          <div class="company-info-item">
            <div class="company-info-icon">
              <BaseIcon icon="phone" />
            </div>
            <div class="company-info-text">
              <div class="info-label">{{ $t('secure_route_notification_settings.phone') }}</div>
              <div class="info-value">{{ customer?.phone || '-' }}</div>
            </div>
          </div>
          <div class="company-info-item">
            <div class="company-info-icon">
              <BaseIcon icon="shield-alt" />
            </div>
            <div class="company-info-text">
              <div class="info-label">{{ $t('secure_route_notification_settings.alarm_center_header') }}</div>
              <div class="info-value">{{ customer?.alarm_center?.name || '-' }}</div>
            </div>
          </div>
        </div>
      </div>

      <!-- Tabs -->
      <div class="tabs-wrapper">
        <button 
          v-for="tab in tabs" 
          :key="tab"
          @click="activeTab = tab"
          :class="['tab-button', { active: activeTab === tab }]"
        >
          <BaseIcon :icon="getTabIcon(tab)" class="tab-icon" />
          {{ getTabLabel(tab) }}
        </button>
      </div>

      <div class="modal-body-content">
        <ValidationObserver ref="formCreateNotificationRecipients">
          <!-- Recipients Tab -->
          <div v-if="activeTab === 'recipients'" class="tabs-content recipients-layout">
          <!-- Left Sidebar -->
          <div class="recipients-sidebar">
            <div class="sidebar-header">
              <div class="sidebar-title">{{ $t('secure_route_notification_settings.recipients') }}</div>
              <button class="btn-add-recipient" @click="addRecipient()" :disabled="isTripCompletedOrOngoing">
                <BaseIcon icon="plus" class="icon-plus" />
              </button>
            </div>
            
            <div class="recipients-list">
              <div v-if="recipients.length === 0" class="no-recipients-message">
                <p>{{ $t('secure_route_notification_settings.no_recipients_yet') }}</p>
                <small>{{ $t('secure_route_notification_settings.add_recipient_hint') }}</small>
              </div>
              <div 
                v-for="(r, idx) in recipients" 
                :key="idx"
                :class="['recipient-item', { active: selectedRecipientIdx === idx, incomplete: !isRecipientValid(r) }]"
                @click="selectedRecipientIdx = idx"
              >
                <div class="recipient-item-avatar">
                  {{ r.name?.charAt(0).toUpperCase() || '?' }}
                </div>
                <div class="recipient-item-content">
                  <div class="recipient-item-name">{{ r.name || $t('secure_route_notification_settings.new_recipient') }}</div>
                  <div v-if="!isRecipientValid(r)" class="recipient-item-warning">{{ getRecipientWarning(r) }}</div>
                  <div v-else class="recipient-item-email">{{ r.email || r.phone }}</div>
                </div>
                <button class="recipient-item-remove" @click.stop="removeRecipient(idx)" :disabled="isTripCompletedOrOngoing">
                  <BaseIcon icon="trash" />
                </button>
              </div>
            </div>

            <!-- Add Recipient Link -->
            <div class="add-recipient-link" @click="addRecipient()" :class="{ 'cursor-not-allowed opacity-50': isTripCompletedOrOngoing }">
              <BaseIcon icon="plus" />
              <span>{{ $t('secure_route_notification_settings.add_recipient') }}</span>
            </div>
          </div>

          <!-- Right Content Area -->
          <div v-if="selectedRecipient" class="recipients-content">
            <div class="content-header">
              <div class="content-title">{{ $t('secure_route_notification_settings.contact_information') }}</div>
              <button class="btn-remove" @click="removeRecipient(selectedRecipientIdx)">
                <BaseIcon icon="trash" />
                {{ $t('secure_route_notification_settings.remove') }}
              </button>
            </div>

            <div class="contact-form">
              <div class="form-group">
                <BaseInput v-model="selectedRecipient.name" type="text" :placeholder="$t('secure_route_notification_settings.name_placeholder')" :field_name="$t('secure_route_notification_settings.field_name_name')" rules="required" />
              </div>
              <div class="form-group">
                <BaseInput v-model="selectedRecipient.email" type="text" :placeholder="$t('secure_route_notification_settings.email_placeholder')" :field_name="$t('secure_route_notification_settings.field_name_email')" :rules="getEmailRules(selectedRecipient)" />
              </div>
              <div class="form-group">
                <BaseInput v-model="selectedRecipient.phone" maxlength="15" type="text" :placeholder="$t('secure_route_notification_settings.phone_placeholder')" :field_name="$t('secure_route_notification_settings.field_name_phone')" :rules="getPhoneRules(selectedRecipient)" />
              </div>
            </div>

            <!-- Notification Preferences -->
            <div class="notification-section">
              <div class="section-header">{{ $t('secure_route_notification_settings.notification_preferences') }}</div>
              <div class="info-banner">
                <BaseIcon icon="info-circle" />
                <span>{{ $t('secure_route_notification_settings.email_sms_info') }}</span>
              </div>

              <!-- Quick Template Bar -->
              <div class="quick-template-bar">
                <span class="quick-template-label">
                  <BaseIcon icon="layer-group" class="qtb-icon" />
                  {{ $t('secure_route_notification_settings.quick_apply') }}
                </span>
                <div class="quick-template-chips">
                  <button
                    v-for="template in allTemplates"
                    :key="template.id"
                    :class="['template-chip', { active: matchedTemplateId === template.id, 'chip-system': template.isSystem }]"
                    @click="toggleTemplateOnRecipient(selectedRecipient, template)"
                    :disabled="isTripCompletedOrOngoing || !isRecipientValid(selectedRecipient)"
                    :title="!isRecipientValid(selectedRecipient) ? $t('secure_route_notification_settings.chip_tooltip_invalid') : (template.descKey ? $t(template.descKey) : template.description)"
                  >
                    {{ template.nameKey ? $t(template.nameKey) : template.name }}
                  </button>
                </div>
                <button
                  class="template-chip-save"
                  @click="showSaveTemplateDialog = !showSaveTemplateDialog"
                  :disabled="isTripCompletedOrOngoing"
                  title="Save current settings as a new template"
                >
                  <BaseIcon icon="bookmark" class="mr-1" />
                  {{ $t('secure_route_notification_settings.save_btn') }}
                </button>
              </div>

              <!-- Save Template Inline Panel -->
              <transition name="slide-down">
                <div v-if="showSaveTemplateDialog" class="save-template-panel">
                  <div class="save-template-panel-title">
                    <BaseIcon icon="bookmark" class="mr-1" />
                    {{ $t('secure_route_notification_settings.save_template_title') }}
                  </div>
                  <div class="save-template-fields">
                    <input v-model="newTemplateName" :placeholder="$t('secure_route_notification_settings.template_name_placeholder')" class="template-field-input" maxlength="40" />
                  </div>
                  <div class="save-template-actions">
                    <button class="btn-stpl-cancel" @click="showSaveTemplateDialog = false">{{ $t('secure_route_notification_settings.cancel') }}</button>
                    <button class="btn-stpl-save" @click="saveCurrentAsTemplate()" :disabled="!newTemplateName.trim() || templatesSaving">
                      <BaseIcon :icon="templatesSaving ? 'spinner' : 'save'" :class="['mr-1', { 'spin-icon': templatesSaving }]" />
                      {{ templatesSaving ? $t('secure_route_notification_settings.saving') : $t('secure_route_notification_settings.save_template') }}
                    </button>
                  </div>
                </div>
              </transition>

              <div class="events-sections">
                <div v-for="section in sections" :key="section.key" class="event-section">
                  <div class="section-title" @click="toggleRecipientSection(selectedRecipient, section.key)">
                    <div class="title-text">{{ $t('secure_route_notification_settings.sections.' + section.key) }}</div>
                    <div class="section-headers">
                      <div class="col-header-email">{{ $t('secure_route_notification_settings.email') }}</div>
                      <div class="col-header-sms">{{ $t('secure_route_notification_settings.sms') }}</div>
                      <BaseIcon :icon="(selectedRecipient.expandedSections && selectedRecipient.expandedSections[section.key]) ? 'chevron-up' : 'chevron-down'" class="chevron-icon" />
                    </div>
                  </div>

                  <div :class="['events-table', { expanded: selectedRecipient.expandedSections && selectedRecipient.expandedSections[section.key] }]">
                    <div v-for="event in section.events" :key="event.key" class="table-row">
                      <div class="col-event">
                        {{ $t('secure_route_notification_settings.events.' + event.key) }}
                        <Tooltip :message="getInfoText(event)" position="right">
                          <BaseIcon icon="info-circle" class="info-icon" />
                        </Tooltip>
                      </div>
                      <div class="col-email">
                        <label class="toggle-switch" :class="{ disabled: !selectedRecipient.email }">
                          <input type="checkbox" v-model="selectedRecipient.settings[event.key].email" :disabled="!selectedRecipient.email || isTripCompletedOrOngoing" class="toggle-input" />
                          <span class="toggle-slider"></span>
                        </label>
                      </div>
                      <div class="col-sms">
                        <label class="toggle-switch" :class="{ disabled: !selectedRecipient.phone }">
                          <input type="checkbox" v-model="selectedRecipient.settings[event.key].sms" :disabled="!selectedRecipient.phone || isTripCompletedOrOngoing" class="toggle-input" />
                          <span class="toggle-slider"></span>
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Alarm Center Tab -->
        <div v-if="activeTab === 'alarm_center' && alarmCenter" class="tabs-content alarm-center-layout">
          <!-- Alarm Center Header -->
          <div class="alarm-center-header">
            <div class="alarm-center-icon">
              <BaseIcon icon="shield-alt" />
            </div>
            <div class="alarm-center-text">
              <div class="alarm-center-title">{{ $t('secure_route_notification_settings.alarm_center_settings') }}</div>
              <div class="alarm-center-subtitle">{{ $t('secure_route_notification_settings.alarm_center_subtitle') }}</div>
            </div>
          </div>

          <!-- Events Container -->
          <div class="alarm-center-events-container">
            <div class="alarm-events-sections">
              <div v-for="section in sections" :key="section.key" class="alarm-event-section">
                <!-- Section Header -->
                <div class="alarm-section-header" @click="toggleAlarmSection(section.key)">
                  <div class="alarm-section-title">{{ $t('secure_route_notification_settings.sections.' + section.key) }}</div>
                  <div class="alarm-section-headers">
                    <button
                      class="alarm-select-all-btn"
                      @click.stop="toggleAlarmSectionAll(section)"
                      :title="isAlarmSectionAllSelected(section) ? $t('secure_route_notification_settings.deselect_all') : $t('secure_route_notification_settings.select_all')"
                    >
                      <span v-if="isAlarmSectionAllSelected(section)">{{ $t('secure_route_notification_settings.deselect_all') }}</span>
                      <span v-else>{{ $t('secure_route_notification_settings.select_all') }}</span>
                    </button>
                    <div class="alarm-header-label">{{ $t('secure_route_notification_settings.alarm') }}</div>
                    <BaseIcon :icon="(alarmCenter.expandedSections && alarmCenter.expandedSections[section.key]) ? 'chevron-up' : 'chevron-down'" class="chevron-icon" />
                  </div>
                </div>

                <!-- Events in this section -->
                <div :class="['alarm-events-table', { expanded: alarmCenter.expandedSections && alarmCenter.expandedSections[section.key] }]">
                  <div v-for="event in section.events" :key="event.key" class="alarm-event-row">
                    <div class="alarm-col-event">
                      {{ $t('secure_route_notification_settings.events.' + event.key) }}
                      <Tooltip :message="getInfoText(event)" position="right">
                        <BaseIcon icon="info-circle" class="info-icon" />
                      </Tooltip>
                    </div>
                    <div class="alarm-col-toggle">
                      <label class="toggle-switch">
                        <input type="checkbox" v-model="alarmCenter.settings[event.key].alarm" :disabled="isTripCompletedOrOngoing" class="toggle-input" />
                        <span class="toggle-slider"></span>
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Templates Tab -->
        <div v-if="activeTab === 'templates'" class="tabs-content templates-layout">

          <!-- System Templates -->
          <div class="templates-section">
            <div class="templates-section-header">
              <div class="templates-section-title">
                <BaseIcon icon="star" class="mr-1" />
                {{ $t('secure_route_notification_settings.system_templates') }}
              </div>
              <span class="readonly-badge">{{ $t('secure_route_notification_settings.readonly_badge') }}</span>
            </div>
            <div class="system-templates-grid">
              <div v-for="template in systemTemplates" :key="template.id" class="system-template-card">
                <div class="stc-header">
                  <span class="stc-name">{{ template.nameKey ? $t(template.nameKey) : template.name }}</span>
                  <span class="stc-count">{{ $t('secure_route_notification_settings.events_count', { count: countEnabledEvents(template.settings) }) }}</span>
                </div>
                <div class="stc-desc">{{ template.descKey ? $t(template.descKey) : template.description }}</div>
                <div class="stc-tags">
                  <span v-for="section in sections" :key="section.key" class="stc-section-tag">
                    <span :class="['stc-dot', { active: section.events.some(e => template.settings[e.key] && (template.settings[e.key].email || template.settings[e.key].sms)) }]"></span>
                    {{ $t('secure_route_notification_settings.sections.' + section.key + '_short') }}
                  </span>
                </div>
                <button class="btn-apply-tpl" @click="applyTemplateToAll(template)" :disabled="isTripCompletedOrOngoing">
                  {{ $t('secure_route_notification_settings.apply_to_all_recipients') }}
                </button>
              </div>
            </div>
          </div>

          <!-- My Templates -->
          <div class="templates-section">
            <div class="templates-section-header">
              <div class="templates-section-title">
                <BaseIcon icon="bookmark" class="mr-1" />
                {{ $t('secure_route_notification_settings.my_templates') }}
              </div>
            </div>

            <div v-if="templatesLoading" class="templates-loading-state">
              <BaseIcon icon="spinner" class="spin-icon" />
              {{ $t('secure_route_notification_settings.loading_templates') }}
            </div>

            <div v-else-if="templates.length === 0" class="templates-empty-state">
              <BaseIcon icon="layer-group" class="templates-empty-icon" />
              <p>{{ $t('secure_route_notification_settings.no_custom_templates') }}</p>
              <small>{{ $t('secure_route_notification_settings.no_custom_templates_hint') }}</small>
            </div>

            <div v-else class="my-templates-list">
              <div v-for="template in templates" :key="template.id" class="my-template-row">
                <div class="mtr-icon">
                  <BaseIcon icon="layer-group" />
                </div>
                <div class="mtr-info">
                  <div class="mtr-name">{{ template.name }}</div>
                  <div v-if="template.description" class="mtr-desc">{{ template.description }}</div>
                </div>
                <div class="mtr-meta">
                  <span class="events-badge">{{ $t('secure_route_notification_settings.events_count', { count: countEnabledEvents(template.settings) }) }}</span>
                </div>
                <div class="mtr-actions">
                  <button class="btn-mtr-apply" @click="applyTemplateToAll(template)" :disabled="isTripCompletedOrOngoing">
                    {{ $t('secure_route_notification_settings.apply_to_all') }}
                  </button>
                  <button class="btn-mtr-delete" @click="deleteTemplate(template.id)">
                    <BaseIcon icon="trash" />
                  </button>
                </div>
              </div>
            </div>
          </div>

        </div>
      </ValidationObserver>
    </div>
    </div>

    <div class="modal-footer px-6 pt-4">
      <button class="btn-red-outline px-3 py-2 float-left" @click="hide()">{{ $t('user_actions.cancel') }}<BaseIcon icon="times-circle" class="ml-1" /></button>
      <button v-if="!isTripCompletedOrOngoing" class="btn-blue px-3 py-2 float-right" @click="save()" >{{ $t('user_actions.save') }}<BaseIcon icon="save" class="ml-1" /></button>
    </div>
  </modal>
</template>

<script>
import Tooltip from '@/components/base/tooltip.vue';

const MODAL_WIDTH = 1400;

const SYSTEM_TEMPLATES = [
  {
    id: 'sys-minimal',
    name: 'Minimal',
    description: 'Only trip lifecycle — low noise',
    nameKey: 'secure_route_notification_settings.system_template_names.minimal',
    descKey: 'secure_route_notification_settings.system_template_desc.minimal',
    isSystem: true,
    settings: {
      trip_started:           { email: true,  sms: true },
      trip_completed:         { email: true,  sms: true },
      trip_canceled:          { email: false, sms: false },
      deviated_from_route:    { email: false, sms: false },
      back_on_route:          { email: false, sms: false },
      unplanned_stop_started: { email: false, sms: false },
      unplanned_stop_ended:   { email: false, sms: false },
      waiting_time_exceeded:  { email: false, sms: false },
      break_started:          { email: false, sms: false },
      break_ended:            { email: false, sms: false },
      break_time_exceeded:    { email: false, sms: false },
      running_late:           { email: false, sms: false },
      eta_missed:             { email: false, sms: false },
      device_offline:         { email: false, sms: false },
      low_battery:            { email: false, sms: false },
      gps_signal_lost:        { email: false, sms: false },
      excessive_speed:        { email: false, sms: false },
      crash_detected:         { email: false, sms: false },
    },
  },
  {
    id: 'sys-safety',
    name: 'Safety Focus',
    description: 'High-priority safety & emergency events only',
    nameKey: 'secure_route_notification_settings.system_template_names.safety_focus',
    descKey: 'secure_route_notification_settings.system_template_desc.safety_focus',
    isSystem: true,
    settings: {
      trip_started:           { email: true, sms: true },
      trip_completed:         { email: true,  sms: true },
      trip_canceled:          { email: false, sms: false },
      deviated_from_route:    { email: true, sms: true },
      back_on_route:          { email: false, sms: false },
      unplanned_stop_started: { email: false, sms: false },
      unplanned_stop_ended:   { email: false, sms: false },
      waiting_time_exceeded:  { email: false, sms: false },
      break_started:          { email: false, sms: false },
      break_ended:            { email: false, sms: false },
      break_time_exceeded:    { email: false, sms: false },
      running_late:           { email: false, sms: false },
      eta_missed:             { email: true,  sms: false },
      device_offline:         { email: true,  sms: true  },
      low_battery:            { email: true,  sms: false },
      gps_signal_lost:        { email: true,  sms: false },
      excessive_speed:        { email: true,  sms: true  },
      crash_detected:         { email: true,  sms: true  },
    },
  },
  {
    id: 'sys-route',
    name: 'Route Compliance',
    description: 'Route deviations, timing & schedule events',
    nameKey: 'secure_route_notification_settings.system_template_names.route_compliance',
    descKey: 'secure_route_notification_settings.system_template_desc.route_compliance',
    isSystem: true,
    settings: {
      trip_started:           { email: true,  sms: true },
      trip_completed:         { email: true,  sms: false },
      trip_canceled:          { email: true,  sms: true },
      deviated_from_route:    { email: true,  sms: true  },
      back_on_route:          { email: true,  sms: false },
      unplanned_stop_started: { email: true,  sms: false },
      unplanned_stop_ended:   { email: false, sms: false },
      waiting_time_exceeded:  { email: true,  sms: false },
      break_started:          { email: false, sms: false },
      break_ended:            { email: false, sms: false },
      break_time_exceeded:    { email: true,  sms: false },
      running_late:           { email: true,  sms: true  },
      eta_missed:             { email: true,  sms: true  },
      device_offline:         { email: false, sms: false },
      low_battery:            { email: false, sms: false },
      gps_signal_lost:        { email: false, sms: false },
      excessive_speed:        { email: false, sms: false },
      crash_detected:         { email: false, sms: false },
    },
  },
  {
    id: 'sys-full',
    name: 'Full Monitoring',
    description: 'All 17 events enabled via email',
    nameKey: 'secure_route_notification_settings.system_template_names.full_monitoring',
    descKey: 'secure_route_notification_settings.system_template_desc.full_monitoring',
    isSystem: true,
    settings: {
      trip_started:           { email: true, sms: true },
      trip_completed:         { email: true, sms: true },
      trip_canceled:          { email: true, sms: true },
      deviated_from_route:    { email: true, sms: true },
      back_on_route:          { email: true, sms: true },
      unplanned_stop_started: { email: true, sms: true },
      unplanned_stop_ended:   { email: true, sms: true },
      waiting_time_exceeded:  { email: true, sms: true },
      break_started:          { email: true, sms: true },
      break_ended:            { email: true, sms: true },
      break_time_exceeded:    { email: true, sms: true },
      running_late:           { email: true, sms: true },
      eta_missed:             { email: true, sms: true },
      device_offline:         { email: true, sms: true },
      low_battery:            { email: true, sms: true },
      gps_signal_lost:        { email: true, sms: true },
      excessive_speed:        { email: true, sms: true },
      crash_detected:         { email: true, sms: true },
    },
  },
];

export default {
  name: "AssetTrackerNotificationModal",
  
  components: {
    Tooltip
  },

  data() {
    return {
      modalWidth: MODAL_WIDTH,
      modalHeight: 800,
      trip: null,
      customer: {},
      expandedSections: {},
      activeTab: 'recipients',
      tabs: ['recipients', 'alarm_center', 'templates'],
      selectedRecipientIdx: 0,
      templates: [],
      showSaveTemplateDialog: false,
      newTemplateName: '',
      applyingTemplate: false,
      templatesLoading: false,
      templatesSaving: false,
      sections: [
        {
          key: 'trip_lifecycle',
          title: 'Trip Lifecycle Events',
          events: [
            { key: 'trip_started', label: 'Trip Started', info: 'Vehicle moves >1km from start point with consistent forward movement' },
            { key: 'trip_completed', label: 'Trip Completed', info: 'Vehicle reaches within geofence distance of final destination' },
            { key: 'trip_canceled', label: 'Trip Canceled', info: 'Manager manually cancels active trip' },
          ],
        },
        {
          key: 'route_compliance',
          title: 'Route Compliance Events',
          events: [
            { key: 'deviated_from_route', label: 'Deviated from Route', info: '3+ consecutive GPS points >300m outside route geofence' },
            { key: 'back_on_route', label: 'Back on Route', info: 'Returns to within geofence after deviation' },
          ],
        },
        {
          key: 'stop_waiting',
          title: 'Stop & Waiting Events',
          events: [
            { key: 'unplanned_stop_started', label: 'Unplanned Stop Started', info: 'Vehicle stationary >10 min (not at planned break location)' },
            { key: 'unplanned_stop_ended', label: 'Unplanned Stop Ended', info: 'Vehicle resumes movement after an unplanned stop' },
            { key: 'waiting_time_exceeded', label: 'Waiting Time Exceeded', info: 'Single stop duration exceeds configured waiting limit' },
          ],
        },
        {
          key: 'planned_break',
          title: 'Planned Break Events',
          events: [
            { key: 'break_started', label: 'Break Started', info: 'Vehicle stops near planned break location' },
            { key: 'break_ended', label: 'Break Ended', info: 'Vehicle resumes movement after planned break' },
            { key: 'break_time_exceeded', label: 'Break Time Exceeded', info: 'Break duration > (planned duration + tolerance)' },
          ],
        },
        {
          key: 'schedule_timing',
          title: 'Schedule & Timing Events',
          events: [
            { key: 'running_late', label: 'Running Late', info: 'Projected arrival > scheduled arrival by >15 minutes' },
            { key: 'eta_missed', label: 'ETA Missed', info: 'Current time > expected arrival time, trip not completed' },
          ],
        },
        {
          key: 'device_comm',
          title: 'Device & Communication Events',
          events: [
            { key: 'device_offline', label: 'Device Offline', info: 'No GPS data received for >30 minutes during active trip' },
            { key: 'low_battery', label: 'Low Battery', info: 'Device reports battery <20% during trip' },
            { key: 'gps_signal_lost', label: 'GPS Signal Lost', info: 'Device reports no GPS fix for >15 minutes' },
          ],
        },
        {
          key: 'safety_compliance',
          title: 'Safety & Compliance Events',
          events: [
            { key: 'excessive_speed', label: 'Driving Too Fast', info: 'Speed >20 km/h above route speed limit' },
            { key: 'crash_detected', label: 'Crash Detected', info: 'Sudden impact or severe deceleration detected by device sensor' },
          ],
        },
      ],
      alarmCenter: null,
      recipients: [],
    };
  },



  computed: {
    isTripCompletedOrOngoing() {
      return this.trip?.trip_state?.tripStarted === true || this.trip?.trip_state?.tripEnded === true;
    },
    selectedRecipient() {
      return this.recipients[this.selectedRecipientIdx] || null;
    },
    allTemplates() {
      return [...SYSTEM_TEMPLATES, ...this.templates];
    },
    systemTemplates() {
      return SYSTEM_TEMPLATES;
    },
    matchedTemplateId() {
      if (!this.selectedRecipient) return null;
      const hasEmail = !!(this.selectedRecipient.email && this.selectedRecipient.email.trim());
      const hasPhone = !!(this.selectedRecipient.phone && this.selectedRecipient.phone.trim());
      // Check user templates first so they are not shadowed by system templates with identical settings
      const ordered = [...this.templates, ...SYSTEM_TEMPLATES];
      for (const t of ordered) {
        if (this.settingsMatchEffective(this.selectedRecipient.settings, t.settings, hasEmail, hasPhone)) {
          return t.id;
        }
      }
      return null;
    },
  },

  watch: {
    trip(newTrip) {
      if (newTrip) {
        this.loadCustomerAndAlarm();
        this.loadNotificationSettings();
      }
    },
    recipients: {
      handler(newRecipients) {
        if (this.applyingTemplate) return;
        newRecipients.forEach(recipient => {
          // Clear email checkboxes if email is empty
          if (!recipient.email || !recipient.email.trim()) {
            Object.keys(recipient.settings).forEach(key => {
              recipient.settings[key].email = false;
            });
          }
          // Clear SMS checkboxes if phone is empty
          if (!recipient.phone || !recipient.phone.trim()) {
            Object.keys(recipient.settings).forEach(key => {
              recipient.settings[key].sms = false;
            });
          }
        });
      },
      deep: true
    }
  },

  methods: {
    setupModal(trip) {
      this.trip = trip || null
    },
    async loadCustomerAndAlarm() {
      try {
        // Fetch both customers and alarm centers in parallel
        const [customerResp, alarmResp] = await Promise.all([
          this.axios.get(`${process.env.VUE_APP_SERVER_URL}/customer/`),
          this.axios.get(`${process.env.VUE_APP_SERVER_URL}/alarm-central/`)
        ]);

        const customers = customerResp.data.data || [];
        const alarmCenters = alarmResp.data.data || [];

        // Find the customer for the current trip
        this.customer = customers.find(c => c.customer_id === this.trip?.customer_id) || {};

        // Attach the customer's alarm center
        this.customer.alarm_center = alarmCenters.find(a => a._id === this.customer?.alarm_central) || {};

        // Load notification templates for this customer
        if (this.customer.customer_id) {
          await this.loadTemplatesFromServer(this.customer.customer_id);
        }

      } catch (error) {
        this.handleError(error);
      }
    },

    loadNotificationSettings() {
      const hasRecipients = this.trip?.notification_settings?.recipients?.length > 0;
      
      if (hasRecipients) {
        this.recipients = this.trip.notification_settings.recipients.map(r => this.createRecipient(r));
      } else {
        this.recipients = [];
      }
      
      // Load alarmCenter data if it exists
      if (Object.keys(this.trip?.notification_settings?.alarmCenter || {}).length) {
        this.alarmCenter = this.createAlarmCenter(this.trip.notification_settings.alarmCenter);
      } else {
        this.alarmCenter = this.createAlarmCenter();
      }
    },

    createAlarmCenter(data = {}) {
      // Create event settings
      const settings = {};
      this.sections.forEach(section => {
        section.events.forEach(event => {
          settings[event.key] = data?.[event.key] || { alarm: false };
        });
      });

      // Create expanded sections state (all sections expanded by default)
      const expandedSections = {};
      this.sections.forEach((section) => {
        expandedSections[section.key] = true;
      });

      return {
        settings,
        expandedSections,
        expanded: true
      };
    },

    createRecipient(data = {}) {
      // Create event settings
      const settings = {};
      this.sections.forEach(section => {
        section.events.forEach(event => {
          settings[event.key] = data.events?.[event.key] || { email: false, sms: false };
        });
      });

      // Create expanded sections state (first two sections expanded)
      const expandedSections = {};
      this.sections.forEach((section) => {
        expandedSections[section.key] = true;
      });

      return {
        name: data.name || '',
        email: data.email || '',
        phone: data.phone || '',
        settings,
        expandedSections,
        expanded: true
      };
    },

    reset() {
      this.trip = null;
      this.customer = {};
      this.expandedSections = {};
      this.recipients = [this.createRecipient()];
      this.alarmCenter = this.createAlarmCenter();
    },

    toggleRecipientSection(recipient, key) {
      if (recipient?.expandedSections) {
        this.$set(recipient.expandedSections, key, !recipient.expandedSections[key]);
      }
    },

    toggleAlarmSection(key) {
      if (this.alarmCenter?.expandedSections) {
        this.$set(this.alarmCenter.expandedSections, key, !this.alarmCenter.expandedSections[key]);
      }
    },

    isAlarmSectionAllSelected(section) {
      if (!this.alarmCenter) return false;
      return section.events.every(e => this.alarmCenter.settings[e.key]?.alarm === true);
    },

    toggleAlarmSectionAll(section) {
      if (!this.alarmCenter || this.isTripCompletedOrOngoing) return;
      const selectAll = !this.isAlarmSectionAllSelected(section);
      section.events.forEach(e => {
        this.$set(this.alarmCenter.settings[e.key], 'alarm', selectAll);
      });
    },

    addRecipient() {
      if(this.isTripCompletedOrOngoing) return;
      this.recipients.push(this.createRecipient());
    },

    removeRecipient(idx) {
      if (idx < this.recipients.length) {
        this.recipients.splice(idx, 1);
        // Auto-select previous recipient if exists
        if (this.recipients.length > 0) {
          if (this.selectedRecipientIdx >= this.recipients.length) {
            this.selectedRecipientIdx = this.recipients.length - 1;
          }
        } else {
          this.selectedRecipientIdx = 0;
        }
      }
    },

    hide() {
      this.reset();
      this.$modal.hide("modal-asset-tracker-notification-setting");
    },

    getInfoText(event) {
      if (!event || !event.key) return '';
      return this.$t('secure_route_notification_settings.event_info.' + event.key);
    },

    getEmailRules(recipient) {
      // If phone is filled, email is optional but must be valid if provided
      if (recipient.phone && recipient.phone.trim()) {
        return 'email';
      }
      // If phone is empty, email is required
      return 'required|email';
    },

    getPhoneRules(recipient) {
      // If email is filled, phone is optional but must be valid if provided
      if (recipient.email && recipient.email.trim()) {
        return '';
      }
      // If email is empty, phone is required
      return 'required';
    },

    async save() {
      const success = await this.$refs.formCreateNotificationRecipients.validate();
      if (!success) {
        return;
      }

      try {
        // Only save recipients that have a name AND at least email or phone
        const validRecipients = this.recipients.filter(r => this.isRecipientValid(r));

        if (this.recipients.length > 0 && validRecipients.length === 0) {
          this.handleError(null, this.$t('secure_route_notification_settings.at_least_one_recipient_error'));
          return;
        }

        const payload = {
          notification_settings: {
            recipients: validRecipients.map(r => ({
              name: r.name,
              email: r.email,
              phone: r.phone,
              events: r.settings
            })),
            alarmCenter: this.alarmCenter?.settings || {}
          }
        }

        const response = await this.axios.put(
          `${process.env.VUE_APP_SERVER_URL}/asset-tracker/notification-settings/${this.trip._id}`,
          payload
        );

        this.handleSuccess(this.$t('secure_route_notification_settings.notification_updated'));
        this.$emit('settings-updated', response.data.trip);
        this.hide();
      } catch (error) {
        console.error('Failed to save notification settings:', error);
        this.handleError(error, this.$t('secure_route_notification_settings.save_template_failed'));
      }
    },

    getTabIcon(tab) {
      const icons = { recipients: 'users', alarm_center: 'shield-alt', templates: 'layer-group' };
      return icons[tab] || 'circle';
    },

    getTabLabel(tab) {
      const labels = {
        recipients: this.$t('secure_route_notification_settings.tab_recipients'),
        alarm_center: this.$t('secure_route_notification_settings.tab_alarm_center'),
        templates: this.$t('secure_route_notification_settings.tab_templates'),
      };
      return labels[tab] || tab;
    },

    settingsMatchTemplate(settings, templateSettings) {
      for (const key of Object.keys(templateSettings)) {
        if (!settings[key]) return false;
        if (settings[key].email !== templateSettings[key].email) return false;
        if (settings[key].sms !== templateSettings[key].sms) return false;
      }
      return true;
    },

    // Compares recipient's current settings against template settings, taking into account
    // that channels without contact info are always false (mirrors applySettingsRespectingContact).
    settingsMatchEffective(settings, templateSettings, hasEmail, hasPhone) {
      for (const key of Object.keys(templateSettings)) {
        const s = settings[key];
        if (!s) return false;
        const expectedEmail = hasEmail ? templateSettings[key].email : false;
        const expectedSms   = hasPhone  ? templateSettings[key].sms   : false;
        if (s.email !== expectedEmail || s.sms !== expectedSms) return false;
      }
      return true;
    },

    // Applies a template's settings to a recipient but zeros out channels
    // the recipient doesn't have a contact for (no email → email:false, no phone → sms:false)
    applySettingsRespectingContact(recipient, templateSettings) {
      const hasEmail = !!(recipient.email && recipient.email.trim());
      const hasPhone = !!(recipient.phone && recipient.phone.trim());
      Object.keys(templateSettings).forEach(key => {
        if (recipient.settings[key] !== undefined) {
          this.$set(recipient.settings, key, {
            email: hasEmail ? templateSettings[key].email : false,
            sms:   hasPhone ? templateSettings[key].sms   : false,
          });
        }
      });
    },

    applyTemplateToRecipient(recipient, template) {
      if (!recipient) return;
      this.applyingTemplate = true;
      this.applySettingsRespectingContact(recipient, template.settings);
      this.$nextTick(() => {
        this.applyingTemplate = false;
      });
    },

    toggleTemplateOnRecipient(recipient, template) {
      if (!recipient) return;
      const hasEmail = !!(recipient.email && recipient.email.trim());
      const hasPhone = !!(recipient.phone && recipient.phone.trim());
      const isActive = this.settingsMatchEffective(recipient.settings, template.settings, hasEmail, hasPhone);
      this.applyingTemplate = true;
      if (isActive) {
        // Deselect: clear all event settings to false
        Object.keys(recipient.settings).forEach(key => {
          this.$set(recipient.settings, key, { email: false, sms: false });
        });
      } else {
        this.applySettingsRespectingContact(recipient, template.settings);
      }
      this.$nextTick(() => {
        this.applyingTemplate = false;
      });
    },

    applyTemplateToAll(template) {
      if (this.recipients.length === 0) {
        this.addRecipient();
      }
      this.recipients.forEach(recipient => {
        this.applyTemplateToRecipient(recipient, template);
      });
      this.activeTab = 'recipients';
    },

    async saveCurrentAsTemplate() {
      if (!this.newTemplateName.trim() || !this.selectedRecipient) return;
      if (!this.customer?.customer_id) return;

      this.templatesSaving = true;
      try {
        const response = await this.axios.post(
          `${process.env.VUE_APP_SERVER_URL}/notification-templates`,
          {
            customer_id: this.customer.customer_id,
            name: this.newTemplateName.trim(),
            settings: JSON.parse(JSON.stringify(this.selectedRecipient.settings)),
          }
        );
        const saved = response.data.template;
        this.templates.push({
          id: saved._id,
          name: saved.name,
          description: saved.description,
          isSystem: false,
          settings: saved.settings,
        });
        this.showSaveTemplateDialog = false;
        this.newTemplateName = '';
        this.handleSuccess(this.$t('secure_route_notification_settings.template_saved'));
      } catch (error) {
        this.handleError(error, this.$t('secure_route_notification_settings.save_template_failed'));
      } finally {
        this.templatesSaving = false;
      }
    },

    async deleteTemplate(id) {
      try {
        await this.axios.delete(`${process.env.VUE_APP_SERVER_URL}/notification-templates/${id}`);
        this.templates = this.templates.filter(t => t.id !== id);
      } catch (error) {
        this.handleError(error, this.$t('secure_route_notification_settings.delete_template_failed'));
      }
    },

    async loadTemplatesFromServer(customerId) {
      this.templatesLoading = true;
      try {
        const response = await this.axios.get(
          `${process.env.VUE_APP_SERVER_URL}/notification-templates/customer/${customerId}`
        );
        this.templates = (response.data.templates || []).map(t => ({
          id: t._id,
          name: t.name,
          description: t.description || '',
          isSystem: false,
          settings: t.settings,
        }));
      } catch (error) {
        this.templates = [];
      } finally {
        this.templatesLoading = false;
      }
    },

    countEnabledEvents(settings) {
      return Object.values(settings).filter(v => v.email || v.sms).length;
    },

    isRecipientValid(r) {
      const hasName = r.name && r.name.trim();
      const hasContact = (r.email && r.email.trim()) || (r.phone && r.phone.trim());
      return !!(hasName && hasContact);
    },

    getRecipientWarning(r) {
      const hasName = r.name && r.name.trim();
      const hasContact = (r.email && r.email.trim()) || (r.phone && r.phone.trim());
      if (!hasName && !hasContact) return this.$t('secure_route_notification_settings.warning_missing_all');
      if (!hasName) return this.$t('secure_route_notification_settings.warning_missing_name');
      return this.$t('secure_route_notification_settings.warning_missing_contact');
    },
  },

  created() {
    this.modalWidth = window.innerWidth < MODAL_WIDTH ? window.innerWidth : MODAL_WIDTH;
    this.alarmCenter = this.createAlarmCenter();
  },
};
</script>

<style scoped>
.asset-tracker-notification-setting {
  display: flex;
  flex-direction: column;
}

.asset-tracker-notification-setting .modal-body {
  background: #f3f4f6 !important;
  padding: 20px 25px 85px 25px !important;
  height: calc(100vh - 100px);
  overflow-y: scroll;
}
.asset-tracker-notification-setting .modal-header {
 margin-bottom: 0px !important;
}
.asset-tracker-notification-setting .modal-footer {
 position: relative !important;
 height: 65px;
}
/* Company Header Card */
.company-header-card {
  background: #ffffff;
  padding: 20px 24px;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  margin-bottom: 16px;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.05);
}

.company-info-row {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 24px;
}

.company-info-item {
  display: flex;
  align-items: flex-start;
  gap: 12px;
}

.company-info-icon {
  width: 36px;
  height: 36px;
  border-radius: 8px;
  background: #f3f4f6;
  color: #6b7280;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  flex-shrink: 0;
  margin-top: 2px;
}

.company-info-text {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.info-label {
  font-size: 11px;
  font-weight: 700;
  color: #9ca3af;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.info-value {
  font-size: 14px;
  font-weight: 600;
  color: #111827;
}

/* Tabs */
.tabs-wrapper {
  display: flex;
  gap: 0;
  padding: 0;
  border-bottom: 1px solid #e5e7eb;
  margin-bottom: 0;
  background: #f9fafb;
}

.tab-button {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  border: none;
  background: #f9fafb;
  color: #9ca3af;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  border-radius: 0;
  transition: all 200ms ease;
  border-bottom: 2px solid transparent;
}

.tab-button:hover {
  background: #f3f4f6;
  color: #6b7280;
}

.tab-button.active {
  color: #374151;
  border-bottom: 2px solid #6b7280;
  background: #ffffff;
}

.tab-icon {
  font-size: 16px;
}

/* Modal Body Content */
.modal-body-content {
  flex: 1;
  overflow: auto;
  display: flex;
  flex-direction: column;
  padding-top: 25px;
}

.tabs-content {
  flex: 1;
  overflow: auto;
  display: flex;
  flex-direction: column;
}

/* Recipients Layout */
.recipients-layout {
  display: grid;
  grid-template-columns: 280px 1fr;
  gap: 16px;
  height: 100%;
}

.recipients-sidebar {
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  height: fit-content;
  padding: 16px;
}

.sidebar-header {
  display: none;
}

.sidebar-title {
  display: none;
}

.btn-add-recipient {
  display: none;
}

.icon-plus {
  font-size: 14px;
}

.recipients-list {
  overflow: visible;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0;
  flex: 1;
}

.recipient-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 0;
  border-radius: 0;
  cursor: pointer;
  transition: all 200ms ease;
  background: transparent;
}

.recipient-item:hover {
  background: transparent;
  border-color: #e5e7eb;
}

.recipient-item.active {
  background: #eff6ff;
  border-color: #e5e7eb;
  padding: 12px 8px;
  margin: 0 -8px;
  border-radius: 10px;
}

.recipient-item-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: #cbd5e1;
  color: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 16px;
  flex-shrink: 0;
}

.recipient-item.active .recipient-item-avatar {
  background: #3b82f6;
  color: #ffffff;
}

.recipient-item-content {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.recipient-item-name {
  font-size: 13px;
  font-weight: 600;
  color: #111827;
  line-height: 1.3;
}

.recipient-item.active .recipient-item-name {
  color: #1e40af;
  font-weight: 700;
}

.recipient-item.incomplete .recipient-item-avatar {
  background: #fde68a;
  color: #92400e;
}

.recipient-item.incomplete.active .recipient-item-avatar {
  background: #f59e0b;
  color: #ffffff;
}

.recipient-item-warning {
  font-size: 11px;
  color: #d97706;
  font-weight: 500;
  line-height: 1.3;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.recipient-item-email {
  font-size: 12px;
  color: #9ca3af;
  line-height: 1.3;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.recipient-item-remove {
  opacity: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 4px;
  border: none;
  background: transparent;
  color: #ef4444;
  cursor: pointer;
  transition: all 200ms ease;
  flex-shrink: 0;
  font-size: 16px;
  padding: 0;
}

.recipient-item:hover .recipient-item-remove {
  opacity: 1;
  background: #fee2e2;
}

.recipient-item-remove:hover {
  background: #fecaca;
  color: #dc2626;
}

.recipient-item-remove:disabled {
  cursor: not-allowed;
  opacity: 0.5;
  background: transparent;
}

/* No Recipients Message */
.no-recipients-message {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  text-align: center;
  color: #9ca3af;
}

.no-recipients-icon {
  font-size: 2.5rem;
  margin-bottom: 12px;
  opacity: 0.5;
}

.no-recipients-message p {
  margin: 0;
  font-weight: 500;
  color: #6b7280;
  font-size: 14px;
}

.no-recipients-message small {
  margin-top: 4px;
  font-size: 12px;
  color: #9ca3af;
}

/* Add Recipient Link */
.add-recipient-link {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 12px 0;
  color: #3b82f6;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  border: none;
  border-top: 1px solid #e5e7eb;
  margin-top: 8px;
  transition: all 200ms ease;
  background: transparent;
}

.add-recipient-link:hover {
  background: transparent;
}

/* Recipients Content Area */
.recipients-content {
  flex: 1;
  overflow: auto;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
}

.content-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px;
  border-bottom: 1px solid #e5e7eb;
  flex-shrink: 0;
}

.content-title {
  font-size: 16px;
  font-weight: 600;
  color: #111827;
}

.btn-remove {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  background: transparent;
  color: #6b7280;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 13px;
  font-weight: 500;
  transition: all 200ms ease;
}

.btn-remove:hover {
  background: #f3f4f6;
  color: #111827;
}

/* Contact Form */
.contact-form {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
  padding: 16px;
  border-bottom: 1px solid #e5e7eb;
  background: #ffffff;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.form-label {
  font-size: 13px;
  font-weight: 600;
  color: #111827;
}

/* Notification Preferences Section */
.notification-section {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px;
  flex: 1;
  overflow: auto;
}

.section-header {
  font-size: 16px;
  font-weight: 600;
  color: #111827;
  padding-bottom: 12px;
  border-bottom: 1px solid #e5e7eb;
}

.info-banner {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  background: #f5f9ff;
  border: 1px solid #dbeafe;
  border-radius: 8px;
  padding: 12px;
  font-size: 13px;
  color: #1e40af;
  margin-bottom: 12px;
}

.info-banner svg {
  flex-shrink: 0;
  margin-top: 2px;
}

/* Event Sections */
.events-sections {
  display: flex;
  flex-direction: column;
  gap: 0;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  overflow: hidden;
}

.event-section {
  background: #ffffff;
  border: none;
  border-radius: 0;
  overflow: hidden;
  border-top: none;
}

.event-section:first-child {
  border-top: none;
  border-radius: 0;
}

.event-section:last-child {
  border-radius: 0;
}

.event-section:not(:last-child) {
  border-bottom: 1px solid #e5e7eb;
}

.section-title {
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 16px;
  align-items: center;
  padding: 12px 16px;
  background: #f3f4f6;
  cursor: pointer;
  user-select: none;
  transition: background 200ms ease;
}

.section-title:hover {
  background: #e5e7eb;
}

.title-text {
  font-size: 12px;
  font-weight: 700;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.section-headers {
  display: grid;
  grid-template-columns: 80px 80px auto;
  gap: 16px;
  align-items: center;
}

.col-header-email,
.col-header-sms {
  font-size: 12px;
  font-weight: 700;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  text-align: right;
}

.chevron-icon {
  font-size: 16px;
  color: #6b7280;
  transition: transform 200ms ease;
}

.events-table {
  max-height: 0;
  overflow: hidden;
  transition: max-height 300ms ease;
}

.events-table.expanded {
  max-height: 2000px;
}

.table-header {
  display: grid;
  grid-template-columns: 2fr 1fr 1fr;
  gap: 16px;
  padding: 12px 16px;
  background: #f3f4f6;
  border-bottom: 1px solid #e5e7eb;
  font-size: 11px;
  font-weight: 700;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.table-row {
  display: grid;
  grid-template-columns: 1fr 80px 80px;
  gap: 16px;
  padding: 12px 16px;
  align-items: center;
  border-bottom: 1px solid #e5e7eb;
  transition: background 200ms ease;
}

.table-row:hover {
  background: #f9fafb;
}

.table-row:last-child {
  border-bottom: none;
}

.col-event {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: #111827;
  font-weight: 500;
}

.info-icon {
  font-size: 14px;
  color: #9ca3af;
  flex-shrink: 0;
}

.col-email,
.col-sms {
  display: flex;
  justify-content: center;
  align-items: center;
}

.col-alarm {
  display: flex;
  justify-content: flex-end;
}

/* Alarm Center Layout */
.alarm-center-layout {
  padding: 15px 20px;
  overflow: auto;
  display: flex;
  flex-direction: column;
  background: #ffffff;
  border-radius: 12px;
}

/* Alarm Center Header */
.alarm-center-header {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  margin-bottom: 20px;
}

.alarm-center-icon {
  width: 48px;
  height: 48px;
  border-radius: 8px;
  background: #fee2e2;
  color: #dc2626;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  flex-shrink: 0;
}

.alarm-center-text {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.alarm-center-title {
  font-size: 16px;
  font-weight: 600;
  color: #111827;
}

.alarm-center-subtitle {
  font-size: 13px;
  color: #6b7280;
}

/* Events Container - Single Box */
.alarm-center-events-container {
  flex: 1;
  overflow: auto;
}

.alarm-events-sections {
  display: flex;
  flex-direction: column;
  gap: 0;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  overflow: hidden;
}

.alarm-event-section {
  background: #ffffff;
  border: none;
  border-radius: 0;
  overflow: hidden;
  border-top: none;
}

.alarm-event-section:first-child {
  border-top: none;
  border-radius: 0;
}

.alarm-event-section:last-child {
  border-radius: 0;
}

.alarm-event-section:not(:last-child) {
  border-bottom: 1px solid #e5e7eb;
}

.alarm-section-header {
  display: grid;
  grid-template-columns: 1fr auto;
  align-items: center;
  padding: 12px 16px;
  background: #f3f4f6;
  cursor: pointer;
  user-select: none;
  transition: background 200ms ease;
}

.alarm-section-header:hover {
  background: #e5e7eb;
}

.alarm-section-headers {
  display: flex;
  gap: 8px;
  align-items: center;
}

.alarm-select-all-btn {
  font-size: 11px;
  font-weight: 600;
  color: #3b82f6;
  background: transparent;
  border: 1px solid #bfdbfe;
  border-radius: 10px;
  padding: 2px 9px;
  cursor: pointer;
  transition: all 150ms ease;
  white-space: nowrap;
  line-height: 1.6;
}

.alarm-select-all-btn:hover {
  background: #eff6ff;
  border-color: #3b82f6;
}

.alarm-header-label {
  font-size: 12px;
  font-weight: 700;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  text-align: center;
  width: 80px;
}

.chevron-icon {
  font-size: 16px;
  color: #6b7280;
  transition: transform 200ms ease;
}

.alarm-events-table {
  max-height: 0;
  overflow: hidden;
  transition: max-height 300ms ease;
}

.alarm-events-table.expanded {
  max-height: 2000px;
}

.alarm-event-row {
  display: grid;
  grid-template-columns: 1fr 80px;
  gap: 16px;
  padding: 12px 16px;
  align-items: center;
  border-bottom: 1px solid #e5e7eb;
  transition: background 200ms ease;
}

.alarm-event-row:hover {
  background: #f9fafb;
}

.alarm-event-row:last-child {
  border-bottom: none;
}

.alarm-col-event {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: #111827;
  font-weight: 500;
}

.alarm-col-toggle {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 80px;
}

/* Toggle Switch */
.toggle-switch {
  position: relative;
  display: inline-block;
  width: 42px;
  height: 24px;
  cursor: pointer;
}

.toggle-switch.disabled {
  cursor: not-allowed;
  opacity: 0.5;
}

.toggle-input {
  opacity: 0;
  width: 0;
  height: 0;
}

.toggle-slider {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #d1d5db;
  border-radius: 24px;
  transition: background-color 200ms ease;
}

.toggle-slider:before {
  content: '';
  position: absolute;
  height: 20px;
  width: 20px;
  left: 2px;
  bottom: 2px;
  background-color: white;
  border-radius: 50%;
  transition: transform 200ms ease;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06);
}

.toggle-input:checked + .toggle-slider {
  background-color: #3b82f6;
}

.toggle-input:checked + .toggle-slider:before {
  transform: translateX(18px);
}

.toggle-switch:hover .toggle-slider {
  background-color: #9ca3af;
}

.toggle-input:checked + .toggle-slider:hover {
  background-color: #2563eb;
}

.toggle-input:focus + .toggle-slider {
  box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.2);
}

/* Responsive */
@media (max-width: 1200px) {
  .recipients-layout {
    grid-template-columns: 220px 1fr;
  }

  .contact-form {
    grid-template-columns: 1fr 1fr;
  }

  .company-info-row {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 768px) {
  .recipients-layout {
    grid-template-columns: 1fr;
  }

  .recipients-sidebar {
    border-right: none;
    border-bottom: 1px solid #e5e7eb;
    max-height: 200px;
  }

  .company-info-row {
    grid-template-columns: 1fr;
  }

  .contact-form {
    grid-template-columns: 1fr;
  }
}

/* ===== QUICK TEMPLATE BAR ===== */
.quick-template-bar {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  margin-bottom: 12px;
  flex-wrap: wrap;
}

.quick-template-label {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  font-weight: 700;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  white-space: nowrap;
  flex-shrink: 0;
}

.qtb-icon {
  font-size: 13px;
}

.quick-template-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  flex: 1;
}

.template-chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 12px;
  border-radius: 20px;
  border: 1px solid #d1d5db;
  background: #ffffff;
  color: #374151;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 150ms ease;
  white-space: nowrap;
}

.template-chip:hover:not(:disabled) {
  border-color: #6b7280;
  background: #f3f4f6;
}

.template-chip.active {
  background: #eff6ff;
  border-color: #3b82f6;
  color: #1d4ed8;
}

.template-chip:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.template-chip-save {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 12px;
  border-radius: 20px;
  border: 1px dashed #9ca3af;
  background: transparent;
  color: #6b7280;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 150ms ease;
  white-space: nowrap;
  flex-shrink: 0;
}

.template-chip-save:hover:not(:disabled) {
  border-color: #3b82f6;
  color: #3b82f6;
  background: #eff6ff;
}

.template-chip-save:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* ===== SAVE TEMPLATE PANEL ===== */
.save-template-panel {
  background: #f0f9ff;
  border: 1px solid #bae6fd;
  border-radius: 8px;
  padding: 14px 16px;
  margin-bottom: 12px;
}

.save-template-panel-title {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 600;
  color: #0369a1;
  margin-bottom: 12px;
}

.save-template-fields {
  display: flex;
  gap: 8px;
  margin-bottom: 10px;
}

.template-field-input {
  flex: 1;
  padding: 7px 10px;
  border: 1px solid #bae6fd;
  border-radius: 6px;
  font-size: 13px;
  background: #ffffff;
  color: #111827;
  outline: none;
  transition: border-color 150ms ease;
}

.template-field-input:focus {
  border-color: #38bdf8;
  box-shadow: 0 0 0 2px rgba(56, 189, 248, 0.15);
}

.save-template-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}

.btn-stpl-cancel {
  padding: 6px 14px;
  border-radius: 6px;
  border: 1px solid #d1d5db;
  background: #ffffff;
  color: #6b7280;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 150ms ease;
}

.btn-stpl-cancel:hover {
  background: #f3f4f6;
}

.btn-stpl-save {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 6px 14px;
  border-radius: 6px;
  border: none;
  background: #0ea5e9;
  color: #ffffff;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 150ms ease;
}

.btn-stpl-save:hover:not(:disabled) {
  background: #0284c7;
}

.btn-stpl-save:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Slide-down transition */
.slide-down-enter-active,
.slide-down-leave-active {
  transition: all 200ms ease;
  overflow: hidden;
}

.slide-down-enter,
.slide-down-leave-to {
  max-height: 0;
  opacity: 0;
}

.slide-down-enter-to,
.slide-down-leave {
  max-height: 300px;
  opacity: 1;
}

/* ===== TEMPLATES TAB ===== */
.templates-layout {
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 0;
}

.templates-section {
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  overflow: hidden;
}

.templates-section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 20px;
  background: #f9fafb;
  border-bottom: 1px solid #e5e7eb;
}

.templates-section-title {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  font-weight: 700;
  color: #111827;
}

.readonly-badge {
  font-size: 11px;
  font-weight: 600;
  color: #6b7280;
  background: #e5e7eb;
  padding: 2px 8px;
  border-radius: 10px;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

/* System Templates Grid */
.system-templates-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
}

.system-template-card {
  display: flex;
  flex-direction: column;
  padding: 16px 18px;
  border-right: 1px solid #e5e7eb;
  gap: 10px;
}

.system-template-card:last-child {
  border-right: none;
}

.stc-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
}

.stc-name {
  font-size: 14px;
  font-weight: 700;
  color: #111827;
}

.stc-count {
  font-size: 11px;
  font-weight: 600;
  color: #ffffff;
  background: #3b82f6;
  padding: 2px 7px;
  border-radius: 10px;
  white-space: nowrap;
  flex-shrink: 0;
}

.stc-desc {
  font-size: 12px;
  color: #6b7280;
  line-height: 1.4;
  flex: 1;
}

.stc-tags {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.stc-section-tag {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  color: #9ca3af;
}

.stc-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #d1d5db;
  flex-shrink: 0;
}

.stc-dot.active {
  background: #10b981;
}

.btn-apply-tpl {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 7px 12px;
  border-radius: 6px;
  border: 1px solid #d1d5db;
  background: #ffffff;
  color: #374151;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 150ms ease;
  margin-top: auto;
}

.btn-apply-tpl:hover:not(:disabled) {
  background: #eff6ff;
  border-color: #3b82f6;
  color: #1d4ed8;
}

.btn-apply-tpl:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* My Templates */
.templates-loading-state {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 40px 30px;
  font-size: 13px;
  color: #9ca3af;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to   { transform: rotate(360deg); }
}

.spin-icon {
  animation: spin 800ms linear infinite;
}

.templates-empty-state {
  justify-content: center;
  padding: 40px 30px;
  text-align: center;
  color: #9ca3af;
  gap: 8px;
}

.templates-empty-icon {
  font-size: 2.5rem;
  opacity: 0.3;
  margin-bottom: 4px;
}

.templates-empty-state p {
  font-size: 14px;
  font-weight: 500;
  color: #6b7280;
  margin: 0;
}

.templates-empty-state small {
  font-size: 12px;
  color: #9ca3af;
  line-height: 1.5;
  max-width: 400px;
}

.my-templates-list {
  display: flex;
  flex-direction: column;
}

.my-template-row {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px 20px;
  border-bottom: 1px solid #f3f4f6;
  transition: background 150ms ease;
}

.my-template-row:last-child {
  border-bottom: none;
}

.my-template-row:hover {
  background: #f9fafb;
}

.mtr-icon {
  width: 36px;
  height: 36px;
  border-radius: 8px;
  background: #eff6ff;
  color: #3b82f6;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  flex-shrink: 0;
}

.mtr-info {
  flex: 1;
  min-width: 0;
}

.mtr-name {
  font-size: 14px;
  font-weight: 600;
  color: #111827;
}

.mtr-desc {
  font-size: 12px;
  color: #9ca3af;
  margin-top: 2px;
}

.mtr-meta {
  flex-shrink: 0;
}

.events-badge {
  font-size: 12px;
  font-weight: 600;
  color: #6b7280;
  background: #f3f4f6;
  padding: 3px 10px;
  border-radius: 10px;
}

.mtr-actions {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
}

.btn-mtr-apply {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 6px 12px;
  border-radius: 6px;
  border: 1px solid #d1d5db;
  background: #ffffff;
  color: #374151;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 150ms ease;
}

.btn-mtr-apply:hover:not(:disabled) {
  background: #eff6ff;
  border-color: #3b82f6;
  color: #1d4ed8;
}

.btn-mtr-apply:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-mtr-delete {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 6px;
  border: 1px solid #fecaca;
  background: #ffffff;
  color: #ef4444;
  font-size: 14px;
  cursor: pointer;
  transition: all 150ms ease;
}

.btn-mtr-delete:hover {
  background: #fee2e2;
  border-color: #ef4444;
}

@media (max-width: 1200px) {
  .system-templates-grid {
    grid-template-columns: repeat(2, 1fr);
  }
  .system-template-card:nth-child(2) {
    border-right: none;
  }
  .system-template-card:nth-child(1),
  .system-template-card:nth-child(2) {
    border-bottom: 1px solid #e5e7eb;
  }
  .save-template-fields {
    flex-direction: column;
  }
}
</style>
